import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Editor from "@/pages/Editor";
import Login from "@/pages/Login";
import Pending from "@/pages/Pending";
import Admin from "@/pages/Admin";
import { AuthProvider, useAuth } from "@/lib/auth";

/**
 * Remove the platform-injected "Made with Emergent" badge on every mount.
 * Removal is authorised in writing by support@emergent.sh (subject:
 * "Permission for Removal of 'Made with Emergent' Branding"). A MutationObserver
 * catches the case where the badge is (re-)injected after initial paint.
 */
function useHideEmergentBadge() {
  React.useEffect(() => {
    const strip = () => {
      const el = document.getElementById("emergent-badge");
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
    strip();
    const obs = new MutationObserver(strip);
    obs.observe(document.body, { childList: true, subtree: false });
    return () => obs.disconnect();
  }, []);
}

/**
 * Route guard tree
 *  Unauthenticated ─────────── /login /register /  (landing)
 *  Signed-in, not approved ── /pending
 *  Signed-in, approved ───── /dashboard /editor/:id
 *  Signed-in, admin ─────── /admin (extra)
 */

function LoadingSplash() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground animate-pulse">
        loading…
      </div>
    </div>
  );
}

function RequireAuth({ children, requireApproved = true, requireAdmin = false }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) return <LoadingSplash />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (requireApproved && user.status !== "approved") {
    return <Navigate to="/pending" replace />;
  }
  if (requireAdmin && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <LoadingSplash />;
  if (user && user.status === "approved") return <Navigate to="/dashboard" replace />;
  if (user && user.status !== "approved") return <Navigate to="/pending" replace />;
  return children;
}

function PendingGate({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <LoadingSplash />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.status === "approved") return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  useHideEmergentBadge();
  return (
    <div className="App dark">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/pending" element={<PendingGate><Pending /></PendingGate>} />
            <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
            <Route path="/editor/:id" element={<RequireAuth><Editor /></RequireAuth>} />
            <Route path="/admin" element={<RequireAuth requireAdmin><Admin /></RequireAuth>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster theme="dark" richColors position="bottom-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
